import * as React from "react";
import { Loader, LoadingOverlay } from "react-overlay-loader";
import { connect } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import "../Waveforms.scss";
import { AnalyseState, LoadingStateEnum } from "../interfaces/state";
import * as Actions from "../actions";
import ActionDashboard from "./ActionDashboard";
import PlaybackDashboard from "./PlaybackDashboard";
import { Dispatch } from "redux";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { useCoughMarshalApiClient } from "../../../misc/api/CoughMarshalApiClient";
import { useAuth } from "react-oidc-context";

export interface WaveformContainerProps {
    sessionId: string;
    primaryAnalystSetId: string;
    isScrollActionZoom: boolean;
    zoomLevel: number;
    selectedWaveform: string;
    loadingState: LoadingStateEnum;
    previousPlaybackTimepoint: number;
    selectedSegment: number;
    isSegmented: boolean;
    isPeaksJsReady: boolean;

    dispatch?: Dispatch<AnalyseState>;
}

export const WaveformContainer = ({ sessionId, primaryAnalystSetId, isScrollActionZoom, zoomLevel, selectedWaveform, loadingState, dispatch, previousPlaybackTimepoint, selectedSegment, isSegmented, isPeaksJsReady }: WaveformContainerProps): JSX.Element => {
    const client = useCoughMarshalApiClient();
    const legacyWebPortalClient = useWebPortalLegacyClient();
    const webPortalClient = useWebPortalApiClient();
    const { user } = useAuth();

    const microphoneRef = React.useRef(null);
    const sensorRef = React.useRef(null);

    const [downloadingMicrophone, setDownloadingMicrophone] = React.useState(false);
    const [downloadingSensor, setDownloadingSensor] = React.useState(false);

    const onDownloading = (element, isStart) => {
        let setter = element == microphoneRef.current? setDownloadingMicrophone:
                     element == sensorRef.current? setDownloadingSensor: null;

        if (setter == null) {
            return;
        }

        setter(isStart);
    };

    const WaveformDownloading = ({element}: any) => {
        const isDownloading = element == microphoneRef.current? downloadingMicrophone:
                              element == sensorRef.current? downloadingSensor: false;

        if (isDownloading != true)
            return null;

        return (
            <i className="fas fa-spin fa-circle-notch"></i>
        );
    };

    React.useEffect(() => {

        dispatch(
            Actions.InitalizePeaksAsyncAction.create(
                client,
                legacyWebPortalClient,
                webPortalClient.client,
                user,
                sessionId,
                primaryAnalystSetId,
                selectedWaveform,
                previousPlaybackTimepoint,
                selectedSegment,
                false,
                (viewSource) => onDownloading(viewSource.element, true),
                (viewSource) => onDownloading(viewSource.element, false)
            )
        );
    }, [sessionId, primaryAnalystSetId, selectedWaveform, dispatch, previousPlaybackTimepoint, selectedSegment]);

    React.useEffect(() => {
        const scrollZoom = (e: WheelEvent) => {
            e.preventDefault();
            if (e.deltaY > 0) {
                dispatch(Actions.SetZoomLevelAction.create(zoomLevel + 1));
            } else {
                dispatch(Actions.SetZoomLevelAction.create(zoomLevel - 1));
            }           
        }

        if (isScrollActionZoom) {
            document.addEventListener("wheel", scrollZoom, { passive: false }); // Passive is required due to this change in Chrome: https://www.chromestatus.com/feature/6662647093133312
        }

        return (): void => document.removeEventListener("wheel", scrollZoom);
    }, [isScrollActionZoom, zoomLevel, dispatch]);


    return (
        <>
            <LoadingOverlay>
                <div className="panel panel-default">
                    <div className="panel-body">
                        {isPeaksJsReady? <PlaybackDashboard />: null}
                        <div id="WaveformWrapper">
                            <div id="WaveformTemplate" className="waveform">
                                <div id="channel-microphone-overview" className="peak peak-overview-container overview-container" />
                                <div id="channel-sensor-overview" className="peak peak-overview-container overview-container" />
                                <h5>Microphone <WaveformDownloading element={microphoneRef.current} /></h5>
                                <div id="channel-microphone-zoomview" ref={microphoneRef} className="peak peak-zoom-container left-container" />
                                <h5>Sensor <WaveformDownloading element={sensorRef.current} /></h5>
                                <div id="channel-sensor-zoomview" ref={sensorRef} className="peak peak-zoom-container right-container" />
                            </div>
                        </div>
                        {isPeaksJsReady? <ActionDashboard />: null}
                    </div>
                </div>
                <Loader loading={loadingState !== LoadingStateEnum.Loaded} />
            </LoadingOverlay>
        </>
    );
}

function mapStateToProps(state: AnalyseState): WaveformContainerProps {
    return {
        sessionId: state.core.sessionMetadata.sessionId,
        primaryAnalystSetId: state.analysisSet.primaryAnalystSetId,
        isScrollActionZoom: state.core.isScrollActionZoom,
        zoomLevel: state.core.zoomLevel,
        selectedWaveform: state.core.selectedWaveform,
        isSegmented: state.core.isSegmented,
        loadingState: state.core.loadingState,
        previousPlaybackTimepoint: state.core.previousPlaybackTimepoint,
        selectedSegment: state.core.selectedSegment,
        isPeaksJsReady: state.core.peaksJs?.ready? true: false
    };
}

export default connect<WaveformContainerProps, any>(mapStateToProps)(WaveformContainer);
